/**
 * EnemySideHud.js
 * Implements the Enemy side Hud element
 */

import { Easing } from "@tweenjs/tween.js";

import { Game, UiElement, UiText, UiImage, UiFrame, GameTween, GameAnimatedSprite, GameText, GameShape, GameTimer } from "pixi_core";

import { Enemy } from "../Enemies/Enemy";
import { Common } from "../game/Common";

import {
    NB_MAX_ENEMIES,
    ENEMY_UI_VISIBILITY,
    EnemyAbilities,
    BubbleColor,
    ENEMY_ABILITY_NAMES,
    BUBBLE_COLOR_NAME_TO_ID,
    ABILITY_NAME_ROTATION,
    ULTIMATE_ABILITIES,
    GameEvents,
    NB_MAX_ENEMY_WEAKNESSES,
    BUBBLE_COLOR_SPRITES,
    NB_ENEMY_TRACKED_TURNS,
    BUBBLE_TINTS,
    ENEMY_FX_SMALL_DAMAGE_THRESHOLD,
    ENEMY_FX_MEDIUM_DAMAGE_THRESHOLD,
    ENEMY_HEAL_FX_OFFSET_X,
    ENEMY_HEAL_FX_OFFSET_Y,
    EnemySprite,
    BossAttackSprite,
    BossHurtSprite,
    ENEMIES_BACK_FRAME_DAMAGED,
    ENEMIES_BACK_FRAME,
    Enemies,
    ENEMY_SHIELD_AND_CHARGE_DISTANCE,
    ENEMY_TIMER_FLASH_SCALE,
    ENEMY_TIMER_FLASH_TIME,
    ENEMY_TIMER_COLOR_FLASH_TIME,
    HUD_ENEMIES_MASK_OFFSET_X,
    HUD_ENEMIES_MASK_WIDTH
} from "../Constants";

export class EnemySideHud {
    /**
     * @public
     * @param {UiElement} parent
     */
    constructor(parent) {
        Common.hudEnemies = this;

        /** @private @type {Number[]} */
        this._enemyDamagedTimer = [0, 0, 0];

        /** @private @type {Boolean[]} */
        this._enemyDamaged = [false, false, false];

        /** @private @type {Boolean[]} */
        this._enemyStunned = [false, false, false];

        /** @private @type {Boolean[]} */
        this._enemyBlackWidowed = [false, false, false];

        /** @private @type {Number[]} */
        this._enemyMaxHP = [1, 1, 1];

        /** @private @type {Number[]} */
        this._enemyCurrentHP = [0, 0, 0];

        /** @private @type {Number[]} */
        this._enemyBoxPosX = [0, 0, 0];

        /** @private @type {Boolean[]} */
        this._enemyDead = [false, false, false];

        /** @private @type {UiElement} */
        this._parent = parent;

        /** @private @type {Number} */
        this._nbEnemies = 1;

        /** @private @type {UiText[]} */
        this._enemyLblHP = [];

        /** @private @type {UiElement[]} */
        this._enemyBox = [];

        /** @private @type {{x: Number, y:Number}[]} */
        this._enemyBoxOriginalPos = [];

        /** @private @type {{x: Number, y:Number}[]} */
        this._enemyTimerOriginalPos = [
            { x: 0, y: 0 },
            { x: 0, y: 0 },
            { x: 0, y: 0 }
        ];

        for (let i = 0; i < NB_MAX_ENEMIES; ++i) {
            let enemyBoxName = "EnemyFrame" + i;
            let enemyBox = parent.findElementByName(enemyBoxName);
            enemyBox.visible = false;
            this._enemyBox.push(enemyBox);

            this._enemyBoxOriginalPos.push({
                x: enemyBox.x,
                y: enemyBox.y
            });
        }

        /** @private @type {UiImage[]} */
        this._enemyPortrait = [];

        /** @private @type {Object[]} */
        this._enemyPortraitOriginalPos = [];

        /** @private @type {UiImage[]} */
        this._flashMasks = [];

        /** @private @type {UiImage} */
        this._flashMask = null;

        /** @private @type {Number} */
        this._flashCounter = 0;

        /** @private @type {UiElement[]} */
        this._abilityFrames = [];

        for (let i = 0; i < NB_MAX_ENEMIES; ++i) {
            let abilityFrameName = "Ability" + i;
            let abilityFrame = parent.findElementByName(abilityFrameName);
            abilityFrame.visible = false;
            this._abilityFrames.push(abilityFrame);
        }

        /** @private @type {UiText[]} */
        this._abilityLabels = [];

        /** @private @type {UiImage[]} */
        this._enemyBackground = [];

        /** @private @type {UiFrame} */
        const frmEnemyBars = parent.findElementByName("EnemyBars");

        /** @private @type {UiFrame} */
        this._frmBars = frmEnemyBars.findElementByName("Bars");

        /** @private @type {GameShape} */
        this._barMask = new GameShape();
        this._frmBars.uiRoot.addChild(this._barMask);
        this._frmBars.uiRoot.mask = this._barMask;

        /** @private @type {UiImage[]} */
        this._barBackground = [
            frmEnemyBars.findElementByName("Background0"),
            frmEnemyBars.findElementByName("Background1"),
            frmEnemyBars.findElementByName("Background2")
        ];

        /** @private @type {UiImage[]} */
        this._barFill = [
            this._frmBars.findElementByName("Bar0"),
            this._frmBars.findElementByName("Bar1"),
            this._frmBars.findElementByName("Bar2")
        ];

        /** @private @type {GameAnimatedSprite[]} */
        this._enemyCounterBackground = [];

        /** @private @type {UiImage[][]} */
        this._enemyStunImageGroup = [];

        /** @private @type {UiImage[]} */
        this._enemyShield = [];

        /** @private @type {UiImage[]} */
        this._enemyPowerUp = [];

        /** @private @type {UiText[]} */
        this._enemyShieldTimer = [];

        /** @private @type {String[][]} */
        this._enemyWeaknesses = [];

        /** @private @type {UiImage[][]} */
        this._enemyWeaknessImages = [];

        /** @private @type {UiElement[][]} */
        this._enemyWeaknessSockets = [];

        /** @private @type {UiImage[][]} */
        this._enemyWeaknessHighlights = null;

        /** @private @type {GameText[]} */
        this._enemyLblAttackTimer = [];

        /** @private @type {UiText[]} */
        this._enemyLblDamage = [];

        /** @private @type {UiImage[]} */
        this._counterHighlight = [];

        /** @private @type {Enemy[]} */
        this._enemies = [null, null, null];

        /** @private @type {Enemy} - Currently attacking enemy */
        this._attacker = null;

        /** @private @type {{index: Number, previousValues: String[], newValues: String[], fxBox: GameAnimatedSprite, fxColors: GameAnimatedSprite[]}} */
        this._weaknessChange = {
            index: 0,
            previousValues: [],
            newValues: [],
            fxBox: null,
            fxColors: []
        };

        /** @private @type {GameAnimatedSprite[]} */
        this._fxHitTop = [];
        /** @private @type {GameAnimatedSprite[]} */
        this._fxHitGlow = [];
        /** @private @type {GameAnimatedSprite[]} */
        this._fxHeal = [];
        /** @private @type {GameAnimatedSprite[]} */
        this._fxShieldUp = [];
        /** @private @type {GameAnimatedSprite[]} */
        this._fxChargeUp = [];

        /** @private @type {GameTimer} */
        this._timerDelayShowFlash = null;
        /** @private @type {GameTimer} */
        this._timerDelayHideFlash = null;
        /** @private @type {GameTween} */
        this._tweenAttackSlideLeft = null;
        /** @private @type {GameTween} */
        this._tweenAttackSlideRight = null;
        /** @private @type {GameTimer} */
        this._timerDelayHideAbilityName = null;

        /** @private @type {GameTimer[]} */
        this._timerHPLabel = [];

        /** @private @type {GameTween[]} */
        this._tweenEnemyDeath = [];

        /** @private @type {GameTween[]} */
        this._tweenEnemyBirth = [];

        /** @private @type {GameTween[]} */
        this._tweenEnemyTimerFlash = [null, null, null];

        /** @private @type {GameTween[]} */
        this._tweenEnemyTimerColor = [null, null, null];

        for (let i = 0; i < NB_MAX_ENEMIES; ++i) {
            let timerHPLabel = null;
            this._timerHPLabel.push(timerHPLabel);
        }

        Game.on(GameEvents.eGAME_SET_NUMBER_OF_ENEMIES, this._onSetNumberOfEnemies, this);
        Game.on(GameEvents.eGAME_SET_ENEMY, this._onSetEnemy, this);
        Game.on(GameEvents.eWEAKNESS_CHANGE_SET_COLOR, this._onWeaknessChangeSetColor, this);
        Game.on(GameEvents.eGAME_DAMAGE_ENEMY, this._onDamage, this);
        Game.on(GameEvents.eGAME_STUN_ENEMY, this._onStunned, this);
        Game.on(GameEvents.eGAME_HEAL_ENEMY, this._onHeal, this);
        Game.on(GameEvents.eGAME_SET_ENEMY_ATTACK_TIMER, this._onSetEnemyAttackTimer, this);
        Game.on(GameEvents.eGAME_HIDE_ENEMIES, this._onHideEnemies, this);
        Game.on(GameEvents.eGAME_SHIELD_ENEMIES, this._onShieldEnemies, this);
        Game.on(GameEvents.eGAME_REMOVE_ENEMY_SHIELD, this._onRemoveShield, this);
        Game.on(GameEvents.eGAME_SET_ENEMY_SHIELD_COUNTER, this._onSetShieldCounter, this);
        Game.on(GameEvents.eDAMAGE_PARTICLE_ENEMY_HIT, this._onDamageParticleHit, this);
        Game.on(GameEvents.eHERO_ABILITY_BLACK_WIDOW, this._onBlackWidowAility, this);

        Game.on(GameEvents.eATTACKPHASE_FLASH_BEGIN, this._onFlashBegin, this);
        Game.on(GameEvents.eATTACKPHASE_ATTACK_BEGIN, this._onAttackBegin, this);
        Game.on(GameEvents.eATTACKPHASE_ABILITY_BEGIN, this._onAbilityBegin, this);
        Game.on(GameEvents.eATTACKPHASE_CUTIN_BEGIN, this._onBeginAllOutAttack, this);
        Game.on(GameEvents.eBOSS_CUT_IN_DONE, this._onResetBossPortrait, this);
        Game.on(GameEvents.eGAME_ABILITY_ENEMY_POWER_UP, this._onEnemyPowerUp, this);
        Game.on(GameEvents.eGAME_ABILITY_ENEMY_POWER_DOWN, this._onEnemyPowerDown, this);

        Game.on(GameEvents.eTUTORIAL_HIGHLIGHT_WEAKNESSES, this._onHighlightWeaknesses, this);
        Game.on(GameEvents.eTUTORIAL_HIGHLIGHT_ATTACK_TIMERS, this._onHighlightAttackTimers, this);
        Game.on(GameEvents.eTUTORIAL_HIGHLIGHT_CLEAR, this._onHighlightClear, this);
    }

    /**
     * @public
     */
    initialize() {
        this._enemies = [null, null, null];
        this._enemyDamagedTimer = [0, 0, 0];
        this._enemyDamaged = [false, false, false];
        this._enemyStunned = [false, false, false];
        this._enemyBlackWidowed = [false, false, false];

        for (let i = 0; i < NB_MAX_ENEMIES; ++i) {
            const enemyBox = this._enemyBox[i];
            this._enemyBoxPosX[i] = this._enemyBox[i].x;

            let enemyBackground = enemyBox.findElementByName("ImgBackground");
            this._enemyBackground.push(enemyBackground);

            let enemyPortrait = enemyBox.findElementByName("EnemyPortrait");
            this._enemyPortrait.push(enemyPortrait);
            this._enemyPortraitOriginalPos.push({ x: enemyPortrait.x, y: enemyPortrait.y });

            const flashMask = enemyBox.findElementByName("FlashMask");
            this._flashMasks.push(flashMask);
            flashMask.visible = false;

            const abilityLabel = this._abilityFrames[i].findElementByName("Name");
            abilityLabel.setLocalized(true);
            this._abilityLabels.push(abilityLabel);
            abilityLabel.uiRoot.angle = ABILITY_NAME_ROTATION;

            const enemyHPLabel = enemyBox.findElementByName("LblEnemyHP");
            this._enemyLblHP.push(enemyHPLabel);

            const enemyShield = enemyBox.findElementByName("Shield");
            this._enemyShield.push(enemyShield);

            const enemyPowerUp = enemyBox.findElementByName("PowerUp");
            this._enemyPowerUp.push(enemyPowerUp);

            const enemyShieldTimer = enemyBox.findElementByName("LblShieldTimer");
            this._enemyShieldTimer.push(enemyShieldTimer);
        }

        for (let i = 0; i < NB_MAX_ENEMIES; ++i) {
            this._enemyShield[i].setVisible(false);
            this._enemyPowerUp[i].setVisible(false);
            this._enemyShieldTimer[i].setVisible(false);
            this._abilityFrames[i].setVisible(false);
            this._enemyBackground[i].setSprite(ENEMIES_BACK_FRAME);

            this._barBackground[i].visible = false;
            this._barFill[i].visible = false;
        }

        this._enemyWeaknessHighlights = [];
        for (let i = 0; i < NB_MAX_ENEMIES; ++i) {
            this._enemyWeaknesses.push([]);
            this._enemyWeaknessSockets.push([]);
            this._enemyWeaknessImages.push([]);
            this._enemyWeaknessHighlights.push([]);

            for (let j = 0; j < NB_MAX_ENEMY_WEAKNESSES; ++j) {
                let enemyWeaknessSocketName = "FrmSocket" + j;
                let enemyWeaknessSocket = this._enemyBox[i].findElementByName(enemyWeaknessSocketName);
                this._enemyWeaknessSockets[i].push(enemyWeaknessSocket);

                let enemyWeaknessName = "Weakness" + j;
                let enemyWeakness = this._enemyBox[i].findElementByName(enemyWeaknessName);
                this._enemyWeaknessImages[i].push(enemyWeakness);

                let weaknessHighlight = enemyWeaknessSocket.findElementByName("Highlight");
                weaknessHighlight.visible = false;
                this._enemyWeaknessHighlights[i].push(weaknessHighlight);
            }
        }

        for (let i = 0; i < this._enemyCounterBackground.length; ++i) {
            this._enemyCounterBackground[i].destroy();
            this._enemyCounterBackground[i] = null;
        }
        this._enemyCounterBackground = [];

        for (let i = 0; i < this._enemyStunImageGroup.length; ++i) {
            for (let j = 0; j < this._enemyStunImageGroup[i].length; ++j) {
                this._enemyStunImageGroup[i][j].destroy();
                this._enemyStunImageGroup[i][j] = null;
            }
        }
        this._enemyStunImageGroup = [];

        for (let i = 0; i < NB_MAX_ENEMIES; ++i) {
            let enemyCounterBackground = this._enemyBox[i].findElementByName("ImgAttackCounter");
            enemyCounterBackground.setVisible(false);
            let enemyCounterAnimation = new GameAnimatedSprite(null, Game.design.getGraphicData("EnemyTimer"));
            enemyCounterAnimation.x = enemyCounterBackground.x;
            enemyCounterAnimation.y = enemyCounterBackground.y;
            enemyCounterAnimation.playAnimation("idle");
            this._enemyBox[i].uiRoot.addChild(enemyCounterAnimation);
            this._enemyCounterBackground.push(enemyCounterAnimation);

            let enemyStunImages = [];
            let enemyStunImage0 = new UiImage({ idSprite: "IMG_EnemyDelay", x: enemyCounterBackground.x, y: enemyCounterBackground.y });
            enemyStunImage0.setVisible(false);
            enemyStunImages.push(enemyStunImage0);
            let enemyStunImage1 = new UiImage({ idSprite: "IMG_EnemyDelay02", x: enemyCounterBackground.x, y: enemyCounterBackground.y });
            enemyStunImage1.setVisible(false);
            enemyStunImages.push(enemyStunImage1);
            let enemyStunImage2 = new UiImage({ idSprite: "IMG_EnemyDelay03", x: enemyCounterBackground.x, y: enemyCounterBackground.y });
            enemyStunImage2.setVisible(false);
            enemyStunImages.push(enemyStunImage2);
            this._enemyBox[i].addChild(enemyStunImage0);
            this._enemyBox[i].addChild(enemyStunImage1);
            this._enemyBox[i].addChild(enemyStunImage2);
            this._enemyStunImageGroup.push(enemyStunImages);
        }

        for (let i = 0; i < this._enemyLblAttackTimer.length; ++i) {
            this._enemyLblAttackTimer[i].destroy();
            this._enemyLblAttackTimer[i] = null;
        }
        this._enemyLblAttackTimer = [];

        for (let i = 0; i < NB_MAX_ENEMIES; ++i) {
            let enemyLblAttackTimerBase = this._enemyBox[i].findElementByName("AttackTimer");
            let enemyLblAttackTimer = new GameText({
                text: "0",
                textStyle: "Font_InGameNumbers",
                fontSize: 26
            });
            enemyLblAttackTimerBase.visible = false;
            enemyLblAttackTimer.x = enemyLblAttackTimerBase.x;
            enemyLblAttackTimer.y = enemyLblAttackTimerBase.y;
            this._enemyTimerOriginalPos[i] = { x: enemyLblAttackTimerBase.x, y: enemyLblAttackTimerBase.y };
            this._enemyBox[i].uiRoot.addChild(enemyLblAttackTimer);
            this._enemyLblAttackTimer.push(enemyLblAttackTimer);
        }

        for (let i = 0; i < NB_MAX_ENEMIES; ++i) {
            let enemyLblDamage = this._enemyBox[i].findElementByName("LblDamage");
            enemyLblDamage.visible = false;
            this._enemyLblDamage.push(enemyLblDamage);
        }

        for (let i = 0; i < NB_MAX_ENEMIES; ++i) {
            const counterHighlight = this._enemyBox[i].findElementByName("CounterHighlight");
            counterHighlight.visible = false;
            this._counterHighlight.push(counterHighlight);
        }

        for (let i = 0; i < NB_MAX_ENEMIES; ++i) {
            const fxHitTop = new GameAnimatedSprite(null, Game.design.getGraphicData("EnemyHitTop"));
            const fxHitGlow = new GameAnimatedSprite(null, Game.design.getGraphicData("EnemyHitGlow"));

            const fxHeal = new GameAnimatedSprite(null, Game.design.getGraphicData("EnemyHeal"));
            fxHeal.x = ENEMY_HEAL_FX_OFFSET_X;
            fxHeal.y = ENEMY_HEAL_FX_OFFSET_Y;
            const fxShieldUp = new GameAnimatedSprite(null, Game.design.getGraphicData("EnemyShieldUpAnimation"));
            const fxChargeUp = new GameAnimatedSprite(null, Game.design.getGraphicData("EnemyChargeUpAnimation"));

            fxHitTop.visible = false;
            fxHitGlow.visible = false;
            fxHeal.visible = false;
            fxShieldUp.visible = false;
            fxChargeUp.visible = false;

            this._fxHitTop.push(fxHitTop);
            this._fxHitGlow.push(fxHitGlow);
            this._fxHeal.push(fxHeal);
            this._fxShieldUp.push(fxShieldUp);
            this._fxChargeUp.push(fxChargeUp);

            this._enemyBox[i].uiRoot.addChild(fxHeal);
            this._enemyBox[i].uiRoot.addChild(fxHitGlow);
            this._enemyBox[i].uiRoot.addChild(fxHitTop);
            this._enemyBox[i].uiRoot.addChild(fxShieldUp);
            this._enemyBox[i].uiRoot.addChild(fxChargeUp);
        }
    }

    /**
     * @public
     * @param {Number} deltaTime
     */
    updateLoop(deltaTime) {
        for (let i = 0; i < NB_MAX_ENEMIES; ++i) {
            if (this._enemyDamaged[i] || this._enemyStunned[i] || this._enemyBlackWidowed[i]) {
                this._shakeEnemy(i, deltaTime);
            }
        }
    }

    /**
     * @private
     * @param {Number} index
     * @param {Number} deltaTime
     */
    _shakeEnemy(index, deltaTime) {
        const elementIndex = this._enemyIndexToElementIndex(index);

        let shakeDistance = Common.settings.shakeDistance;

        if (this._enemyStunned[index] || this._enemyBlackWidowed[index]) {
            shakeDistance = shakeDistance / 2;
        }

        this._enemyDamagedTimer[elementIndex] += deltaTime;
        let randomX = Math.random() * shakeDistance - shakeDistance / 2;
        let randomY = Math.random() * shakeDistance - shakeDistance / 2;

        this._enemyPortrait[elementIndex].x = this._enemyPortraitOriginalPos[elementIndex].x + randomX;
        this._enemyPortrait[elementIndex].y = this._enemyPortraitOriginalPos[elementIndex].y + randomY;

        if (this._enemyBlackWidowed[index]) {
            this._enemyLblAttackTimer[elementIndex].x = this._enemyTimerOriginalPos[elementIndex].x + randomX;
            this._enemyLblAttackTimer[elementIndex].y = this._enemyTimerOriginalPos[elementIndex].y + randomY;
        }

        if (this._enemyDamagedTimer[elementIndex] >= Common.settings.enemyShakeDuration) {
            this._enemyPortrait[elementIndex].x = this._enemyPortraitOriginalPos[elementIndex].x;
            this._enemyPortrait[elementIndex].y = this._enemyPortraitOriginalPos[elementIndex].y;
            this._enemyLblAttackTimer[elementIndex].x = this._enemyTimerOriginalPos[elementIndex].x;
            this._enemyLblAttackTimer[elementIndex].y = this._enemyTimerOriginalPos[elementIndex].y;
            this._enemyDamaged[elementIndex] = false;
            this._enemyStunned[elementIndex] = false;
            this._enemyBlackWidowed[elementIndex] = false;
            this._enemyLblDamage[elementIndex].visible = false;

            for (let i = 0; i < this._enemyStunImageGroup[elementIndex].length; ++i) {
                this._enemyStunImageGroup[elementIndex][i].setVisible(this._enemies[elementIndex].blackWidowStunTimer > i);
            }

            this._onResetBossPortrait(this._enemies[elementIndex]);
            this._checkForDeath(elementIndex);
        }
    }

    /**
     * @private
     * @param {Number} index
     * @param {Number} amount
     * @param {Enemy} enemy
     */
    _onDamage(index, amount, enemy) {
        const elementIndex = this._enemyIndexToElementIndex(index);

        if (this._enemyDead[index]) {
            return;
        }

        this._enemyLblDamage[elementIndex].visible = true;
        this._enemyLblDamage[elementIndex].text = amount.toString();
        let fontSize = this._setDamageFontSize(-amount);
        this._enemyLblDamage[elementIndex].setFontSize(fontSize);

        if (enemy.isABoss) {
            this._enemyPortrait[elementIndex].setSprite(BossHurtSprite[enemy.name]);
        }

        // Begin shaking
        this._enemyDamaged[elementIndex] = true;
        this._enemyDamagedTimer[elementIndex] = 0;
        this._enemyBackground[elementIndex].setSprite(ENEMIES_BACK_FRAME_DAMAGED);

        this._adjustHP(index, amount);

        Game.audio.playSfx("IDSND_ATK_IMPACT");
    }

    /**
     * @private
     * @param {Number} amount
     * @returns {Number}
     */
    _setDamageFontSize(amount) {
        let fontMultiplier = 1;

        if (amount < Common.settings.damageFontNormalDamage) {
            fontMultiplier =
                Common.settings.damageFontMinSizeMultiplier +
                (amount / Common.settings.damageFontNormalDamage) * (1 - Common.settings.damageFontMinSizeMultiplier);
        } else {
            if (amount > Common.settings.damageFontMaxDamage) {
                amount = Common.settings.damageFontMaxDamage;
            }
            fontMultiplier = 1 + (amount / Common.settings.damageFontMaxDamage) * (Common.settings.damageFontMaxSizeMultiplier - 1);
        }

        return Math.round(Common.settings.damageFontNormalSize * fontMultiplier);
    }

    /**
     * @private
     * @param {Enemy} enemy
     */
    _onStunned(enemy) {
        let index = 0;
        for (let i = 0; i < this._enemies.length; ++i) {
            if (enemy === this._enemies[i]) {
                index = i;
            }
        }
        const elementIndex = this._enemyIndexToElementIndex(index);

        if (this._enemyDead[index]) {
            return;
        }

        // Begin shaking
        this._enemyStunned[elementIndex] = true;
        this._enemyDamagedTimer[elementIndex] = 0;
    }

    /**
     * A damage particle has reached the enemy box
     * @private
     * @param {Number} enemyIndex
     * @param {BubbleColor} color
     * @param {Number} chargedParticleScale
     */
    _onDamageParticleHit(enemyIndex, color, chargedParticleScale) {
        const fxHitTop = this._fxHitTop[enemyIndex];
        const fxHitGlow = this._fxHitGlow[enemyIndex];

        fxHitGlow.tint = BUBBLE_TINTS[color] ? BUBBLE_TINTS[color] : 0xffffff;

        fxHitTop.visible = true;
        fxHitGlow.visible = true;

        let animationName = "Big";
        if (chargedParticleScale <= ENEMY_FX_SMALL_DAMAGE_THRESHOLD) {
            animationName = "Small";
        } else if (chargedParticleScale <= ENEMY_FX_MEDIUM_DAMAGE_THRESHOLD) {
            animationName = "Medium";
        }

        fxHitTop.playAnimation(animationName, false, { hideOnCompleted: true });
        fxHitGlow.playAnimation(animationName, false, { hideOnCompleted: true });

        // Random rotation
        const randomAngle = Math.random() * 360;
        fxHitTop.angle = randomAngle;
        fxHitGlow.angle = randomAngle;

        if (this._enemies[enemyIndex].isABoss) {
            this._enemyPortrait[enemyIndex].setSprite(BossHurtSprite[this._enemies[enemyIndex].name]);
        }

        // Begin shaking
        this._enemyDamaged[enemyIndex] = true;
        this._enemyDamagedTimer[enemyIndex] = 0;
        this._enemyBackground[enemyIndex].setSprite(ENEMIES_BACK_FRAME_DAMAGED);
    }

    /**
     * @private
     * @param {Number} enemyIndex
     * @param {Number} amount
     */
    _onHeal(enemyIndex, amount) {
        const elementIndex = this._enemyIndexToElementIndex(enemyIndex);

        if (this._enemyDead[enemyIndex]) {
            return;
        }

        Game.audio.playSfx("IDSND_ENEMY_HP_RISE");
        const fxHeal = this._fxHeal[enemyIndex];
        fxHeal.visible = true;
        fxHeal.playAnimation("Heal", false, { hideOnCompleted: true });

        this._enemyLblDamage[elementIndex].visible = true;
        this._enemyLblDamage[elementIndex].text = "+" + amount.toString();

        if (this._timerHPLabel[elementIndex]) {
            this._timerHPLabel[elementIndex].destroy();
            this._timerHPLabel[elementIndex] = null;
        }

        this._timerHPLabel[elementIndex] = Game.windows.getCurrentWindow().createTimer(Common.settings.attackPhaseDelayBetweenAttacks);
        this._timerHPLabel[elementIndex].onComplete = () => {
            this._enemyLblDamage[elementIndex].visible = false;
        };

        this._adjustHP(enemyIndex, amount);
    }

    /**
     * @private
     * @param {Number[]} index
     * @param {Number} duration
     */
    _onShieldEnemies(index, duration) {
        let elementIndex = 0;

        for (let i = 0; i < index.length; ++i) {
            elementIndex = this._enemyIndexToElementIndex(index[i]);

            if (this._enemyDead[index[i]]) {
                return;
            }

            const fxShieldUp = this._fxShieldUp[elementIndex];

            fxShieldUp.visible = true;
            fxShieldUp.playAnimation("shieldUp", false);
            fxShieldUp.onComplete = () => {
                fxShieldUp.visible = false;
                this._enemyShield[elementIndex].setVisible(true);
                this._enemyShieldTimer[elementIndex].setVisible(true);
                this._enemyShieldTimer[elementIndex].setText(duration.toString());

                if (this._enemyPowerUp[elementIndex].visible) {
                    this._enemyPowerUp[elementIndex].y = this._enemyShield[elementIndex].y + ENEMY_SHIELD_AND_CHARGE_DISTANCE;
                }
            };
        }
    }

    /**
     * @private
     * @param {Number} index
     */
    _onRemoveShield(index) {
        const elementIndex = this._enemyIndexToElementIndex(index);

        if (this._enemyDead[index]) {
            return;
        }

        this._enemyShield[elementIndex].setVisible(false);
        this._enemyShieldTimer[elementIndex].setVisible(false);

        if (this._enemyPowerUp[elementIndex].visible) {
            this._enemyPowerUp[elementIndex].y = this._enemyShield[elementIndex].y;
        }
    }

    /**
     * @private
     * @param {Number} duration
     * @param {Enemy} enemy
     */
    _onSetShieldCounter(duration, enemy) {
        for (let i = 0; i < this._enemies.length; ++i) {
            if (enemy === this._enemies[i]) {
                let elementIndex = this._enemyIndexToElementIndex(i);
                this._enemyShieldTimer[elementIndex].setText(duration.toString());

                if (duration === 0) {
                    this._enemyShield[elementIndex].setVisible(false);
                    this._enemyShieldTimer[elementIndex].setVisible(false);

                    if (this._enemyPowerUp[elementIndex].visible) {
                        this._enemyPowerUp[elementIndex].y = this._enemyShield[elementIndex].y;
                    }
                }
            }
        }
    }

    /**
     * @private
     * @param {Enemy} enemy
     */
    _onEnemyPowerUp(enemy) {
        Game.audio.playSfx("IDSND_ENEMY_CHARGE");
        let elementIndex = 0;

        for (let i = 0; i < this._enemies.length; ++i) {
            if (enemy === this._enemies[i]) {
                elementIndex = this._enemyIndexToElementIndex(i);
                const fxChargeUp = this._fxChargeUp[elementIndex];
                if (enemy.name === Enemies.eTHANOS || this._enemyShield[elementIndex].visible) {
                    this._enemyPowerUp[elementIndex].y = this._enemyShield[elementIndex].y + ENEMY_SHIELD_AND_CHARGE_DISTANCE;
                } else {
                    this._enemyPowerUp[elementIndex].y = this._enemyShield[elementIndex].y;
                }

                fxChargeUp.visible = true;
                fxChargeUp.playAnimation("chargeUp", false);
                fxChargeUp.onComplete = () => {
                    fxChargeUp.visible = false;
                    this._enemyPowerUp[elementIndex].setVisible(true);
                };
            }
        }
    }

    /**
     * @private
     * @param {Enemy} enemy
     */
    _onEnemyPowerDown(enemy) {
        for (let i = 0; i < this._enemies.length; ++i) {
            if (enemy === this._enemies[i]) {
                this._enemyPowerUp[this._enemyIndexToElementIndex(i)].setVisible(false);
            }
        }
    }

    /**
     * @private
     * @param {Number} index
     * @param {Number} amount
     */
    _onSetHP(index, amount) {
        const elementIndex = this._enemyIndexToElementIndex(index);

        this._enemyMaxHP[elementIndex] = amount;
        this._enemyCurrentHP[elementIndex] = amount;
        this._enemyLblDamage[elementIndex].text = "+" + amount;
        this._enemyLblHP[elementIndex].setText(this._enemyCurrentHP[elementIndex].toString());

        this._adjustHP(index, 0);
    }

    /**
     * @private
     * @param {Number} index
     * @param {Number} amount
     */
    _adjustHP(index, amount) {
        const elementIndex = this._enemyIndexToElementIndex(index);

        this._enemyCurrentHP[elementIndex] += amount;

        if (this._enemyCurrentHP[elementIndex] > this._enemyMaxHP[elementIndex]) {
            this._enemyCurrentHP[elementIndex] = this._enemyMaxHP[elementIndex];
        } else if (this._enemyCurrentHP[elementIndex] <= 0) {
            this._enemyCurrentHP[elementIndex] = 0;
            this._enemyDead[elementIndex] = true;
        }

        this._enemyLblHP[elementIndex].setText(this._enemyCurrentHP[elementIndex].toString());

        this._redrawMask();
    }

    /**
     * @private
     */
    _redrawMask() {
        this._barMask.clear();
        this._barMask.beginFill(0x000000);

        for (let i = 0; i < NB_MAX_ENEMIES; ++i) {
            const fillRatio = this._enemyCurrentHP[i] / this._enemyMaxHP[i];

            const barFill = this._barFill[i];

            this._barMask.drawRect(
                HUD_ENEMIES_MASK_OFFSET_X,
                barFill.y + barFill.height / 2,
                HUD_ENEMIES_MASK_WIDTH,
                -barFill.height * fillRatio
            );
        }

        this._barMask.endFill();
    }

    /**
     * If there is only one enemy, it will be displayed in the middle at index 1
     * @private
     * @param {Number} enemyIndex
     * @returns {Number}
     */
    _enemyIndexToElementIndex(enemyIndex) {
        if (this._nbEnemies === 1) {
            return 1;
        } else {
            return enemyIndex;
        }
    }

    /**
     * @private
     * @param {Number} nbEnemies
     * @param {Boolean} addedEnemyConfiguration
     */
    _onSetNumberOfEnemies(nbEnemies, addedEnemyConfiguration = false) {
        this._nbEnemies = nbEnemies;

        for (let i = 0; i < NB_MAX_ENEMIES; ++i) {
            const enemyBox = this._enemyBox[i];
            const barBackground = this._barBackground[i];
            const barFill = this._barFill[i];

            if (nbEnemies > 0) {
                if (!addedEnemyConfiguration) {
                    enemyBox.opacity = 1;
                    barBackground.opacity = 1;
                    barFill.opacity = 1;

                    enemyBox.visible = ENEMY_UI_VISIBILITY[nbEnemies - 1][i];

                    barBackground.visible = enemyBox.visible;
                    barFill.visible = enemyBox.visible;
                }
            } else {
                enemyBox.visible = false;
                barBackground.visible = false;
                barFill.visible = false;
            }
        }
    }

    /**
     * @private
     * @param {Number} index
     * @param {Enemy} enemy
     * @param {String[]} enemyWeakness
     * @param {Boolean} isFirstwave
     */
    _onSetEnemy(index, enemy, enemyWeakness, isFirstwave) {
        const elementIndex = this._enemyIndexToElementIndex(index);

        this._enemies[index] = enemy;
        this._enemyPortrait[elementIndex].setSprite(EnemySprite[enemy.name]);
        this._enemyBackground[elementIndex].setSprite(ENEMIES_BACK_FRAME);
        this._enemyDead[elementIndex] = false;

        // Clear buff-debuff icons
        this._enemyShield[elementIndex].setVisible(false);
        this._enemyPowerUp[elementIndex].setVisible(false);
        this._enemyShieldTimer[elementIndex].setVisible(false);

        for (let j = 0; j < this._enemyStunImageGroup[elementIndex].length; ++j) {
            this._enemyStunImageGroup[elementIndex][j].setVisible(false);
        }

        this._onSetWeaknesses(index, enemyWeakness);
        this._onSetHP(index, enemy.maxHp);

        this._addEnemy(elementIndex, isFirstwave);
    }

    /**
     * @private
     * @param {Number} index
     * @param {String[]} enemyWeakness
     */
    _onSetWeaknesses(index, enemyWeakness) {
        const elementIndex = this._enemyIndexToElementIndex(index);

        this._enemyWeaknesses[elementIndex] = enemyWeakness;

        for (let i = 0; i < this._enemyWeaknessSockets[elementIndex].length; ++i) {
            this._enemyWeaknessSockets[elementIndex][i].visible = false;
        }

        if (enemyWeakness.length > NB_MAX_ENEMY_WEAKNESSES) {
            this._enemyWeaknessSockets[elementIndex][0].visible = true;
            this._enemyWeaknessImages[elementIndex][0].setSprite(BUBBLE_COLOR_SPRITES[BubbleColor.eMULTICOLOR]);
        } else {
            for (let i = 0; i < enemyWeakness.length; ++i) {
                this._enemyWeaknessSockets[elementIndex][i].visible = true;
                this._enemyWeaknessImages[elementIndex][i].setSprite(BUBBLE_COLOR_SPRITES[BUBBLE_COLOR_NAME_TO_ID[enemyWeakness[i]]]);
            }
        }
    }

    /**
     * An enemy weaknesses have changed (Enemy Abilities)
     * ==> Apply visual effects and change the weaknesses colors
     * @public
     * @param {Number} index
     * @param {String[]} enemyWeakness
     * @param {Function} callback
     */
    changeWeaknesses(index, enemyWeakness, callback) {
        this._weaknessChange.index = this._enemyIndexToElementIndex(index);
        this._weaknessChange.previousValues = this._enemyWeaknesses[this._weaknessChange.index];
        this._weaknessChange.newValues = enemyWeakness;
        this._weaknessChange.fxColors = [];

        const enemyBox = this._enemyBox[this._weaknessChange.index];

        const fxBox = new GameAnimatedSprite(null, Game.design.getGraphicData("EnemyWeaknessChange"));
        this._weaknessChange.fxBox = fxBox;

        Game.audio.playSfx("IDSND_ABIL_WEAKNESS_CHANGE");
        enemyBox.uiRoot.addChild(fxBox);
        fxBox.playAnimation("change", false);
        fxBox.onComplete = () => {
            if (this._weaknessChange.fxBox) {
                Game.audio.playSfx("IDSND_BOOST_DEFENSE_SHEILD");
                this._weaknessChange.fxBox.destroy();
                this._weaknessChange.fxBox = null;
            }

            if (callback) {
                callback();
            }
        };
    }

    /**
     * @private
     */
    _onWeaknessChangeSetColor() {
        const elementIndex = this._weaknessChange.index;
        const previousWeaknesses = this._weaknessChange.previousValues;
        const newWeaknesses = this._weaknessChange.newValues;

        let nbPreviousColors = previousWeaknesses.length;
        let nbNewColors = newWeaknesses.length;

        // If more than 3 weaknesses, a single multicolor slot is used
        if (nbPreviousColors > NB_MAX_ENEMY_WEAKNESSES) nbPreviousColors = 1;
        if (nbNewColors > NB_MAX_ENEMY_WEAKNESSES) nbNewColors = 1;

        // Number of color change effects
        // Maximum between the previous number of weaknesses and the updated number of weaknesses
        let numColorEffects = Math.max(nbPreviousColors, nbNewColors);

        // Change color effects
        for (let colorIndex = 0; colorIndex < numColorEffects; ++colorIndex) {
            const colorImage = this._enemyWeaknessImages[elementIndex][colorIndex];

            const fxColor = new GameAnimatedSprite(null, Game.design.getGraphicData("EnemyWeaknessColorChange"));
            colorImage.uiRoot.addChild(fxColor);
            fxColor.playAnimation("change");
            fxColor.onComplete = () => {
                fxColor.destroy();
            };
            this._weaknessChange.fxColors.push(fxColor);
        }

        this._enemyWeaknesses[elementIndex] = newWeaknesses;

        for (let i = 0; i < numColorEffects; ++i) {
            this._enemyWeaknessSockets[elementIndex][i].visible = false;
        }

        if (newWeaknesses.length > NB_MAX_ENEMY_WEAKNESSES) {
            this._enemyWeaknessSockets[elementIndex][0].visible = true;
            this._enemyWeaknessImages[elementIndex][0].setSprite(BUBBLE_COLOR_SPRITES[BubbleColor.eMULTICOLOR]);
        } else {
            for (let i = 0; i < newWeaknesses.length; ++i) {
                this._enemyWeaknessSockets[elementIndex][i].visible = true;
                this._enemyWeaknessImages[elementIndex][i].setSprite(BUBBLE_COLOR_SPRITES[BUBBLE_COLOR_NAME_TO_ID[newWeaknesses[i]]]);
            }
        }
    }

    /**
     * Hide enemies from enemy hud between waves
     * @private
     */
    _onHideEnemies() {
        for (let i = 0; i < NB_MAX_ENEMIES; ++i) {
            this._enemyShield[i].setVisible(false);
            this._enemyPowerUp[i].setVisible(false);
            this._enemyShieldTimer[i].setVisible(false);

            this._barBackground[i].visible = false;
            this._barFill[i].visible = false;

            this._enemyLblAttackTimer[i].visible = false;

            if (this._enemyWeaknessSockets.length > 0) {
                for (let j = 0; j < this._enemyWeaknessSockets[i].length; ++j) {
                    this._enemyWeaknessSockets[i][j].visible = false;
                }
            }
            for (let j = 0; j < this._enemyStunImageGroup[i].length; ++j) {
                this._enemyStunImageGroup[i][j].setVisible(false);
            }
        }
    }

    /**
     * @private
     * @param {Number} index
     * @param {Number} timer
     * @param {Boolean} playAnimation
     */
    _onSetEnemyAttackTimer(index, timer, playAnimation) {
        const elementIndex = this._enemyIndexToElementIndex(index);
        this._enemyLblAttackTimer[elementIndex].visible = true;
        this._enemyLblAttackTimer[elementIndex].text = timer.toString();

        if (timer > NB_ENEMY_TRACKED_TURNS) {
            timer = NB_ENEMY_TRACKED_TURNS;
        }

        if (timer <= 1) {
            this._setEnemyTimerColorFlash(elementIndex, timer === 0);
        } else {
            if (this._tweenEnemyTimerColor[elementIndex]) {
                this._tweenEnemyTimerColor[elementIndex].stop();
                this._tweenEnemyTimerColor[elementIndex].destroy();
                this._tweenEnemyTimerColor[elementIndex] = null;
            }
            this._enemyLblAttackTimer[elementIndex].tint = 0xffffff;
        }

        if (playAnimation) {
            this._setEnemyTimerFlash(elementIndex);
        } else {
            this._enemyCounterBackground[elementIndex].playAnimation("idle");
        }
    }

    /**
     * @private
     * @param {Number} elementIndex
     */
    _setEnemyTimerFlash(elementIndex) {
        if (this._tweenEnemyTimerFlash[elementIndex]) {
            this._tweenEnemyTimerFlash[elementIndex].destroy();
            this._tweenEnemyTimerFlash[elementIndex] = null;
        }

        this._enemyCounterBackground[elementIndex].playAnimation("tickDown");
        this._tweenEnemyTimerFlash[elementIndex] = Game.windows
            .getCurrentWindow()
            .createTween(this._enemyLblAttackTimer[elementIndex])
            .to({ scale: { x: ENEMY_TIMER_FLASH_SCALE, y: ENEMY_TIMER_FLASH_SCALE } }, ENEMY_TIMER_FLASH_TIME)
            .easing(Easing.Sinusoidal.InOut)
            .yoyo(true)
            .repeat(1)
            .start();
    }

    /**
     * @private
     * @param {Number} elementIndex
     * @param {Boolean} timerAtZero
     */
    _setEnemyTimerColorFlash(elementIndex, timerAtZero) {
        this._enemyLblAttackTimer[elementIndex].tint = 0xfff000;
        if (this._tweenEnemyTimerColor[elementIndex]) {
            this._tweenEnemyTimerColor[elementIndex].destroy();
            this._tweenEnemyTimerColor[elementIndex] = null;
        }
        this._tweenEnemyTimerColor[elementIndex] = Game.windows
            .getCurrentWindow()
            .createTween(this._enemyLblAttackTimer[elementIndex])
            .repeat(Infinity)
            .yoyo(true)
            .to({ tint: 0xff0000 }, timerAtZero ? ENEMY_TIMER_COLOR_FLASH_TIME / 2 : ENEMY_TIMER_COLOR_FLASH_TIME)
            .easing(Easing.Sinusoidal.InOut)
            .start();
    }

    /**
     * The enemy is about to begin his attack phase
     * @private
     * @param {Enemy} enemy
     */
    _onFlashBegin(enemy) {
        this._attacker = enemy;

        const elementIndex = this._enemyIndexToElementIndex(enemy.index);

        Game.audio.playSfx("IDSND_ENEMY_ATK_ALERT");
        this._flashMask = this._flashMasks[elementIndex];
        this._flashCounter = Common.settings.attackPhaseFlashCount;
        this._flashMask.visible = true;

        this._timerDelayHideFlash = Game.windows
            .getCurrentWindow()
            .createTimer(Common.settings.attackPhaseFlashHideDelay, { autoStart: false, deleteOnCompletion: false });
        this._timerDelayHideFlash.onComplete = () => {
            this._flashMask.visible = false;

            --this._flashCounter;
            if (this._flashCounter > 0) {
                this._timerDelayShowFlash.start();
            } else {
                this._onFlashCompleted();
            }
        };

        this._timerDelayShowFlash = Game.windows
            .getCurrentWindow()
            .createTimer(Common.settings.attackPhaseFlashShowDelay, { deleteOnCompletion: false });
        this._timerDelayShowFlash.onComplete = () => {
            this._flashMask.visible = true;
            this._timerDelayHideFlash.start();
        };
    }

    /**
     * @private
     */
    _onFlashCompleted() {
        for (let i = 0; i < this._flashMasks.length; ++i) {
            this._flashMasks[i].visible = false;
        }

        if (this._timerDelayShowFlash) {
            this._timerDelayShowFlash.destroy();
            this._timerDelayShowFlash = null;
        }

        if (this._timerDelayHideFlash) {
            this._timerDelayHideFlash.destroy();
            this._timerDelayHideFlash = null;
        }

        Game.fire(GameEvents.eATTACKPHASE_FLASH_COMPLETED, this._attacker);
    }

    /**
     * Performs the regular attack cut-in animation
     * @private
     * @param {Enemy} enemy
     */
    _onAttackBegin(enemy) {
        this._attacker = enemy;

        if (this._attacker.isABoss) {
            this._enemyPortrait[enemy.index].setSprite(BossAttackSprite[enemy.name]);
        }

        const elementIndex = this._enemyIndexToElementIndex(enemy.index);
        const enemyBox = this._enemyBox[elementIndex];
        const enemyBoxIntialX = this._enemyBoxOriginalPos[elementIndex].x;

        const barBackground = this._barBackground[elementIndex];
        const barFill = this._barFill[elementIndex];

        this._tweenAttackSlideLeft = Game.windows
            .getCurrentWindow()
            .createTween(enemyBox)
            .to({ x: enemyBox.x - Common.settings.attackPhaseSlideDistance }, Common.settings.attackPhaseSlideDelay)
            .easing(GameTween.Easing.QuadraticOut)
            .onUpdate(() => {
                barBackground.x = enemyBox.x - enemyBoxIntialX;
                barFill.x = enemyBox.x - enemyBoxIntialX;
            })
            .onComplete(() => {
                this._attacker.fireDamageParticle(enemyBox.x, enemyBox.y);
                this._tweenAttackSlideRight.start();
            });

        this._tweenAttackSlideRight = Game.windows
            .getCurrentWindow()
            .createTween(enemyBox)
            .to({ x: enemyBox.x }, Common.settings.attackPhaseSlideDelay)
            .easing(GameTween.Easing.QuadraticIn)
            .onUpdate(() => {
                barBackground.x = enemyBox.x - enemyBoxIntialX;
                barFill.x = enemyBox.x - enemyBoxIntialX;
            })
            .onComplete(() => {
                this._onSlideCompleted();
                this._onResetBossPortrait(this._attacker);
                Game.fire(GameEvents.eATTACKPHASE_NEXT_ACTION, this._attacker);
            });

        Game.audio.playSfx("IDSND_SMALL_ENEMY_CUT_IN");
        this._tweenAttackSlideLeft.start();
    }

    /**
     * @private
     * @param {Number} enemyIndex
     * @param {Boolean} isFirstwave
     */
    _addEnemy(enemyIndex, isFirstwave) {
        if (!isFirstwave) {
            const enemyBox = this._enemyBox[enemyIndex];
            const enemyBoxIntialX = this._enemyBoxOriginalPos[enemyIndex].x;

            const barBackground = this._barBackground[enemyIndex];
            const barFill = this._barFill[enemyIndex];

            enemyBox.visible = true;
            barBackground.visible = true;
            barFill.visible = true;

            enemyBox.opacity = 0;
            barBackground.opacity = 0;
            barFill.opacity = 0;

            enemyBox.x = this._enemyBoxPosX[enemyIndex] + Common.settings.newWaveSlideDistance;

            const birthTween = Game.windows
                .getCurrentWindow()
                .createTween(enemyBox)
                .to({ x: this._enemyBoxPosX[enemyIndex], opacity: 1 }, Common.settings.newWaveSlideTime)
                .onUpdate(() => {
                    barBackground.x = enemyBox.x - enemyBoxIntialX;
                    barFill.x = enemyBox.x - enemyBoxIntialX;

                    barBackground.opacity = enemyBox.opacity;
                    barFill.opacity = enemyBox.opacity;
                })
                .onComplete(() => this._finishEnemyBirth())
                .start();

            this._tweenEnemyBirth.push(birthTween);
        }
    }

    /**
     * @private
     * @param {Number} enemyIndex
     */
    _checkForDeath(enemyIndex) {
        if (this._enemyCurrentHP[enemyIndex] <= 0) {
            Game.audio.playSfx("IDSND_ENEMY_HP_ZERO_DEFEAT");

            const enemyBox = this._enemyBox[enemyIndex];
            const enemyBoxIntialX = this._enemyBoxOriginalPos[enemyIndex].x;

            const barBackground = this._barBackground[enemyIndex];
            const barFill = this._barFill[enemyIndex];

            const deathTween = Game.windows
                .getCurrentWindow()
                .createTween(enemyBox)
                .to(
                    { x: this._enemyBoxPosX[enemyIndex] + Common.settings.newWaveSlideDistance, opacity: 0 },
                    Common.settings.enemyDeathSlideTime
                )
                .onUpdate(() => {
                    barBackground.x = enemyBox.x - enemyBoxIntialX;
                    barFill.x = enemyBox.x - enemyBoxIntialX;

                    barBackground.opacity = enemyBox.opacity;
                    barFill.opacity = enemyBox.opacity;
                })
                .onComplete(() => this._finishEnemyDeath())
                .start();

            this._tweenEnemyDeath.push(deathTween);
        } else {
            this._enemyBackground[enemyIndex].setSprite(ENEMIES_BACK_FRAME);
        }
    }

    /**
     * @private
     */
    _finishEnemyBirth() {
        for (const tween of this._tweenEnemyBirth) {
            tween.destroy();
        }
        this._tweenEnemyBirth = [];
    }

    /**
     * @private
     */
    _finishEnemyDeath() {
        for (const tween of this._tweenEnemyDeath) {
            tween.destroy();
        }
        this._tweenEnemyDeath = [];

        Game.fire(GameEvents.eGAME_ENEMY_DEAD);
    }

    /**
     * @private
     */
    _onSlideCompleted() {
        if (this._tweenAttackSlideLeft) {
            this._tweenAttackSlideLeft.destroy();
            this._tweenAttackSlideLeft = null;
        }

        if (this._tweenAttackSlideRight) {
            this._tweenAttackSlideRight.destroy();
            this._tweenAttackSlideRight = null;
        }
    }

    /**
     * Performs the ability cut-in animation
     * @private
     * @param {Enemy} enemy
     * @param {EnemyAbilities} abilityId
     */
    _onAbilityBegin(enemy, abilityId) {
        this._attacker = enemy;
        let isUltimateAbility = false;

        const elementIndex = this._enemyIndexToElementIndex(enemy.index);
        const enemyBox = this._enemyBox[elementIndex];
        const abilityStartPos = { x: enemyBox.x, y: enemyBox.y };

        for (let i = 0; i < ULTIMATE_ABILITIES.length; ++i) {
            if (abilityId === ULTIMATE_ABILITIES[i]) {
                isUltimateAbility = true;
                break;
            }
        }

        if (isUltimateAbility) {
            this._attacker.onAbilityNameCompleted(abilityStartPos);
        } else {
            Game.audio.playSfx("IDSND_SMALL_ENEMY_CUT_IN");
            if (this._attacker.isABoss) {
                this._enemyPortrait[enemy.index].setSprite(BossAttackSprite[enemy.name]);
            }

            const elementIndex = this._enemyIndexToElementIndex(enemy.index);

            const abilityFrame = this._abilityFrames[elementIndex];
            abilityFrame.visible = true;

            const abilityLabel = this._abilityLabels[elementIndex];
            abilityLabel.text = ENEMY_ABILITY_NAMES[abilityId];

            if (this._timerDelayHideAbilityName) {
                this._timerDelayHideAbilityName.destroy();
                this._timerDelayHideAbilityName = null;
            }

            this._timerDelayHideAbilityName = Game.windows.getCurrentWindow().createTimer(Common.settings.attackPhaseAbilityNameDelay);
            this._timerDelayHideAbilityName.onComplete = () => {
                abilityFrame.visible = false;
                this._onResetBossPortrait(enemy);
                this._attacker.onAbilityNameCompleted(abilityStartPos);
            };
        }
    }

    /**
     * Sets the boss' portrait during an all-out attack
     * @private
     */
    _onBeginAllOutAttack() {
        if (this._attacker.isABoss) {
            this._enemyPortrait[this._attacker.index].setSprite(BossAttackSprite[this._attacker.name]);
        }
    }

    /**
     * Resets the boss' portrait after an attack/all-out
     * @private
     * @param {Enemy} enemy
     */
    _onResetBossPortrait(enemy) {
        if (enemy && enemy.isABoss && enemy.hp > 0) {
            this._enemyPortrait[enemy.index].setSprite(EnemySprite[enemy.name]);
        }
    }

    /**
     * @private
     */
    _onBlackWidowAility() {
        for (let i = 0; i < this._enemies.length; ++i) {
            if (this._enemies[i] && this._enemies[i].isAlive) {
                this._enemyBlackWidowed[i] = true;
                this._enemyDamagedTimer[i] = 0;
                for (let j = 0; j < this._enemyStunImageGroup[i].length; ++j) {
                    this._enemyStunImageGroup[i][j].setVisible(this._enemies[i].blackWidowStunTimer > j);
                }
            }
        }
    }

    /**
     * @private
     */
    _onHighlightWeaknesses() {
        if (this._enemyWeaknessHighlights) {
            for (let i = 0; i < NB_MAX_ENEMIES; ++i) {
                for (let j = 0; j < NB_MAX_ENEMY_WEAKNESSES; ++j) {
                    this._enemyWeaknessHighlights[i][j].visible = true;
                }
            }
        }
    }

    /**
     * @private
     */
    _onHighlightAttackTimers() {
        for (const counterHighlight of this._counterHighlight) {
            counterHighlight.visible = true;
        }
    }

    /**
     * @private
     */
    _onHighlightClear() {
        for (const counterHighlight of this._counterHighlight) {
            counterHighlight.visible = false;
        }

        if (this._enemyWeaknessHighlights) {
            for (let i = 0; i < NB_MAX_ENEMIES; ++i) {
                for (let j = 0; j < NB_MAX_ENEMY_WEAKNESSES; ++j) {
                    this._enemyWeaknessHighlights[i][j].visible = false;
                }
            }
        }
    }

    /**
     * @public
     */
    closeLevel() {
        this._onSlideCompleted();
        this._onHighlightClear();

        if (this._timerDelayHideAbilityName) {
            this._timerDelayHideAbilityName.stop();
            this._timerDelayHideAbilityName.destroy();
            this._timerDelayHideAbilityName = null;
        }

        if (this._weaknessChange.fxBox && !this._weaknessChange.fxBox.destroyed) {
            this._weaknessChange.fxBox.destroy();
            this._weaknessChange.fxBox = null;
        }
        if (this._weaknessChange.fxColors) {
            for (const fxColor of this._weaknessChange.fxColors) {
                if (!fxColor.destroyed) {
                    fxColor.destroy();
                }
            }
            this._weaknessChange.fxColors = [];
        }

        for (const fx of this._fxHitTop) {
            fx.destroy();
        }
        this._fxHitTop = [];

        for (const fx of this._fxHitGlow) {
            fx.destroy();
        }
        this._fxHitGlow = [];

        for (const fx of this._fxHeal) {
            fx.destroy();
        }
        this._fxHeal = [];

        for (const fx of this._fxShieldUp) {
            fx.destroy();
        }
        this._fxShieldUp = [];

        for (const fx of this._fxChargeUp) {
            fx.destroy();
        }
        this._fxChargeUp = [];

        if (this._timerDelayHideFlash) {
            this._timerDelayHideFlash.destroy();
            this._timerDelayHideFlash = null;
        }

        if (this._timerDelayShowFlash) {
            this._timerDelayShowFlash.destroy();
            this._timerDelayShowFlash = null;
        }

        for (const tween of this._timerHPLabel) {
            if (tween) {
                tween.destroy();
            }
        }
        this._timerHPLabel = [];

        for (const tween of this._tweenEnemyBirth) {
            if (tween) {
                tween.destroy();
            }
        }
        this._tweenEnemyBirth = [];

        for (const tween of this._tweenEnemyDeath) {
            if (tween) {
                tween.destroy();
            }
        }
        this._tweenEnemyDeath = [];

        // Make sure flash masks are hidden at the beginning of a level
        for (const flashMask of this._flashMasks) {
            flashMask.visible = false;
        }

        // Make sure all enemy boxes are at their original positions
        for (let i = 0; i < NB_MAX_ENEMIES; ++i) {
            let enemyBox = this._enemyBox[i];
            let originalPos = this._enemyBoxOriginalPos[i];

            const barBackground = this._barBackground[i];
            const barFill = this._barFill[i];

            if (enemyBox && originalPos) {
                enemyBox.x = originalPos.x;
                enemyBox.y = originalPos.y;
            }

            if (barBackground) {
                barBackground.x = 0;
            }

            if (barFill) {
                barFill.x = 0;
            }
        }

        this._enemyBackground = [];
        this._enemyPortrait = [];
        this._enemyPortraitOriginalPos = [];
        this._flashMasks = [];
        this._abilityLabels = [];
        this._enemyLblHP = [];
        this._enemyShield = [];
        this._enemyPowerUp = [];
        this._enemyShieldTimer = [];
        this._enemyWeaknesses = [];
        this._enemyWeaknessSockets = [];
        this._enemyWeaknessImages = [];
        this._enemyWeaknessHighlights = null;

        // Make sure ability frames are hidden
        for (const abilityFrame of this._abilityFrames) {
            abilityFrame.visible = false;
        }

        for (let i = 0; i < this._enemyCounterBackground.length; ++i) {
            this._enemyCounterBackground[i].destroy();
            this._enemyCounterBackground[i] = null;
        }
        this._enemyCounterBackground = [];

        for (let i = 0; i < this._enemyStunImageGroup.length; ++i) {
            for (let j = 0; j < this._enemyStunImageGroup[i].length; ++j) {
                this._enemyStunImageGroup[i][j].destroy();
                this._enemyStunImageGroup[i][j] = null;
            }
        }
        this._enemyStunImageGroup = [];

        for (let i = 0; i < this._enemyLblAttackTimer.length; ++i) {
            this._enemyLblAttackTimer[i].destroy();
            this._enemyLblAttackTimer[i] = null;
        }
        this._enemyLblAttackTimer = [];

        for (let i = 0; i < this._tweenEnemyTimerFlash.length; ++i) {
            if (this._tweenEnemyTimerFlash[i]) {
                this._tweenEnemyTimerFlash[i].destroy();
                this._tweenEnemyTimerFlash[i] = null;
            }
        }

        for (let i = 0; i < this._tweenEnemyTimerColor.length; ++i) {
            if (this._tweenEnemyTimerColor[i]) {
                this._tweenEnemyTimerColor[i].destroy();
                this._tweenEnemyTimerColor[i] = null;
            }
        }

        if (this._tweenAttackSlideLeft) {
            this._tweenAttackSlideLeft.destroy();
            this._tweenAttackSlideLeft = null;
        }

        if (this._tweenAttackSlideRight) {
            this._tweenAttackSlideRight.destroy();
            this._tweenAttackSlideRight = null;
        }
    }
}
